import { isMatch, merge, pick } from 'lodash';
import qs from 'qs';
import { useCallback, useMemo } from 'react';
import { useHistory, useLocation, useParams } from 'react-router';

import usePersistent from 'hooks/use-persistent';
import { ROUTES } from 'pages/Search/constants';
import { noneIf } from 'pages/Search/utils';
import normalizeSearch from 'utils/normalize-search';
import { safeDecodeUriComponent } from 'utils/safe-decode-uri-component';

interface Payload {
    vendor: string;
    page: number;
    size: number;
    sort?: {
        field: 'product' | 'cves';
        direction: 'asc' | 'desc';
    };
    letter?: string;
    year?: number;
    month?: number;
    score?: number;
    search: string;
}

const defaults: Payload = {
    vendor: '',
    page: 1,
    size: 20,
    letter: undefined,
    sort: {
        field: 'cves',
        direction: 'desc'
    },
    search: '',
    year: undefined,
    month: undefined,
    score: undefined
};

export function useVendorProductCatalogPayload() {
    const history = useHistory();
    const location = useLocation();
    const parameters = useParams<{ vendor: string; letter: string; page: string }>();
    const query = useMemo(() => qs.parse(location.search, { ignoreQueryPrefix: true }), [location.search]);

    const vendor = parameters.vendor ? safeDecodeUriComponent(parameters.vendor) : defaults.vendor;
    const page = parameters.page ? parseInt(parameters.page) : defaults.page;
    const size = query.size ? parseInt(query.size.toString()) : defaults.size;
    const letter = parameters.letter ? parameters.letter : defaults.letter;
    const search = query.search ? normalizeSearch(query.search.toString()) : defaults.search;
    const sort = query.sort || defaults.sort;
    const year = query.year ? parseInt(query.year.toString()) : defaults.year;
    const month = query.month ? parseInt(query.month.toString()) : defaults.month;
    const score = query.score ? parseInt(query.score.toString()) : defaults.score;

    const payload = useMemo(
        () => ({ vendor, page, size, year, month, score, letter, search, sort } as Payload),
        [letter, month, page, score, search, size, sort, vendor, year]
    );

    const payloadRef = usePersistent(payload);

    const url = useCallback(
        (patch: Partial<Payload>) => {
            const previous = payloadRef.current;
            const current = merge({}, previous, patch);

            return ROUTES.VENDOR({
                vendor: current.vendor,
                page: isMatch(previous, pick(current, ['letter', 'search', 'size']))
                    ? noneIf(current.page, defaults.page)
                    : undefined,
                size: noneIf(current.size, defaults.size),
                year: noneIf(current.year, previous.year),
                month: noneIf(current.month, previous.month),
                score: noneIf(current.score, previous.score),
                search: current.search ? noneIf(normalizeSearch(current.search), defaults.search) : undefined,
                sort: noneIf(current.sort, defaults.sort),
                letter: noneIf(current.letter, defaults.letter)
            });
        },
        [payloadRef]
    );

    const set = useCallback((patch: Partial<Payload>) => history.push(url(patch)), [history, url]);

    const actions = useMemo(() => ({ url, set }), [set, url]);

    return [payload, actions] as const;
}
